import { useState } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import DSDialog from "views/system-design/DSDialog";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSBox from "views/system-design/DSBox";
import DSTextField from "views/system-design/DSTextField";
import DSButton from "views/system-design/DSButton";
import DSSecondaryButton from "views/system-design/DSSecondaryButton";
import DSTypography from "views/system-design/DSTypography";
import DSIconButton from "views/system-design/DSIconButton";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import TitleIcon from "@mui/icons-material/Title";
import LabelIcon from "@mui/icons-material/Label";
import { useTickets } from "../../context/TicketsContext";
import { getAssignableUsers } from "../../utils";
import useUsers from "components/hooks/useUsers";
import { Author } from "api/author";
import SectionHeader from "../shared/SectionHeader";
import LabelChips from "../shared/LabelChips";
import UserPickerField from "../shared/UserPickerField";
import firebaseService from "firebase_service/firebaseService";

interface CreateKlydeaModalProps {
  onClose: () => void;
}

export default function CreateKlydeaModal({ onClose }: CreateKlydeaModalProps) {
  const theme = useTheme();
  const { createTicket } = useTickets();
  const { data: allUsers } = useUsers();
  const assigneeOptions = getAssignableUsers(allUsers ?? []);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const currentUserId = firebaseService.getUserId();
  const currentUser =
    assigneeOptions.find((user) => user.id === currentUserId) ?? null;
  const [selectedReporter, setSelectedReporter] = useState<Author | null>(
    currentUser,
  );
  const [selectedAssignee, setSelectedAssignee] = useState<Author | null>(null);
  const [labels, setLabels] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = title.trim().length > 0 && !submitting;

  const handleCreate = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await createTicket({
        title: title.trim(),
        description,
        status: "idea",
        type: "feature",
        assignee: selectedAssignee?.id ?? "",
        labels,
        creatorId: selectedReporter?.id ?? "",
      });
      onClose();
    } catch {
      setError("Failed to create klydea. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleLabel = (label: string) => {
    setLabels((prev) =>
      prev.includes(label)
        ? prev.filter((existing) => existing !== label)
        : [...prev, label],
    );
  };

  return (
    <DSDialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "visible",
        },
      }}
    >
      <DSBox
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.info.dark} 0%, ${theme.palette.info.main} 100%)`,
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <DSBox sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <LightbulbIcon
            sx={{ color: alpha(theme.palette.common.white, 0.9), fontSize: 22 }}
          />
          <DSTypography
            variant="h6"
            sx={{ color: "common.white", fontWeight: 700, fontSize: 18 }}
          >
            New Klydea
          </DSTypography>
        </DSBox>
        <DSIconButton
          size="small"
          onClick={onClose}
          sx={{ color: "grey.300", "&:hover": { color: "common.white" } }}
        >
          <CloseIcon fontSize="small" />
        </DSIconButton>
      </DSBox>

      <DSDialogContent sx={{ p: 0 }}>
        <DSBox sx={{ px: 3, pt: 3, pb: 2 }}>
          <SectionHeader
            icon={<TitleIcon sx={{ fontSize: 16 }} />}
            title="Idea"
          />
          <DSTextField
            placeholder="What's the idea?"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            fullWidth
            autoFocus
            required
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                fontSize: 16,
                fontWeight: 500,
              },
            }}
          />
          <DSTextField
            placeholder="Describe the idea..."
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: 14,
                bgcolor: "white.5",
              },
            }}
          />
        </DSBox>

        <DSBox
          sx={{
            px: 3,
            py: 2.5,
            display: "flex",
            gap: 3,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <UserPickerField
            label="Reporter"
            placeholder="No reporter"
            selectedUser={selectedReporter}
            options={assigneeOptions}
            onChange={setSelectedReporter}
          />
          <UserPickerField
            label="Assignee"
            placeholder="Unassigned"
            selectedUser={selectedAssignee}
            options={assigneeOptions}
            onChange={setSelectedAssignee}
          />
        </DSBox>

        <DSBox sx={{ px: 3, pb: 2.5 }}>
          <SectionHeader
            icon={<LabelIcon sx={{ fontSize: 16 }} />}
            title="Labels"
          />
          <LabelChips
            selectedLabels={labels}
            onToggleLabel={handleToggleLabel}
          />
        </DSBox>
      </DSDialogContent>

      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "white.5",
        }}
      >
        {error && (
          <DSTypography variant="caption" sx={{ color: "error.main" }}>
            {error}
          </DSTypography>
        )}
        <DSBox sx={{ flex: 1 }} />
        <DSSecondaryButton onClick={onClose} sx={{ borderRadius: 2, px: 3 }}>
          Cancel
        </DSSecondaryButton>
        <DSButton
          onClick={handleCreate}
          disabled={!canSubmit}
          sx={{
            borderRadius: 2,
            px: 4,
            fontWeight: 700,
            textTransform: "none",
            boxShadow: canSubmit ? 2 : 0,
          }}
        >
          {submitting ? (
            <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DSCircularProgress size={16} color="inherit" />
              Creating...
            </DSBox>
          ) : (
            "Create Klydea"
          )}
        </DSButton>
      </DSBox>
    </DSDialog>
  );
}
